import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { ArrowRight, ArrowUpRight, FileImage, ChevronRight, ChevronDown, ShieldCheck, Star, Phone, Home as HomeIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO_PAGES, PAGE_LABELS } from "@/data/seo-pages";
import { BLOG_POSTS } from "@/data/blog-posts";
import { renderRichText } from "@/components/RichText";
import { pageSchemas, removePrerenderedSchemas } from "@/lib/schema-helpers";
import { DOCTOR } from "@/data/content";
import { useBooking } from "@/context/BookingContext";

const BASE_URL = "https://bestspinesurgeonhyderabad.com";

// Manage <head> tags for SEO
const useSeoTags = (page) => {
  useEffect(() => {
    if (!page) return;
    const previousTitle = document.title;
    document.title = page.metaTitle;

    // description
    let desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", page.metaDescription);

    // canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${BASE_URL}/${page.slug}`);

    // JSON-LD (shared builders; drop build-time copies to avoid duplicates)
    removePrerenderedSchemas();
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-page", page.slug);
    el.text = JSON.stringify(pageSchemas(page));
    document.head.appendChild(el);

    return () => {
      document.title = previousTitle;
      if (prevDesc !== null) desc.setAttribute("content", prevDesc);
      if (prevCanonical !== null) canonical.setAttribute("href", prevCanonical);
      document.head.removeChild(el);
    };
  }, [page]);
};

const SeoPage = () => {
  const { slug } = useParams();
  const page = SEO_PAGES[slug];
  const { openBooking } = useBooking();

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useSeoTags(page);

  if (!page) return <NotFound />;

  return (
    <>
      {/* Hero */}
      <section className="relative bg-white pt-32 pb-16 md:pt-40 md:pb-20 border-b border-slate-100">
        <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_85%_-10%,#e8f1fb_0%,#f4f8fd_45%,#ffffff_80%)]" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center flex-wrap gap-1 text-sm text-slate-500" data-testid="breadcrumbs">
            <Link to="/" className="flex items-center gap-1 hover:text-brand transition-colors">
              <HomeIcon className="w-3.5 h-3.5" /> Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5" />
            {page.breadcrumbParent && (
              <>
                <Link to={page.breadcrumbParent.path} className="hover:text-brand transition-colors">
                  {page.breadcrumbParent.name}
                </Link>
                <ChevronRight className="w-3.5 h-3.5" />
              </>
            )}
            <span className="text-[#0b2c4d] font-medium">{PAGE_LABELS[page.slug]}</span>
          </nav>

          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{page.eyebrow}</p>
          <h1 className="mt-3 font-heading font-extrabold text-[#0b2c4d] text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[1.02]">
            {page.h1}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
            {page.subtitle}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
            {page.heroCall && (
              <Button
                asChild
                data-testid="seo-hero-call-btn"
                className="bg-brand hover:bg-brand-hover text-white font-semibold h-12 px-6 rounded-full shadow-lg shadow-brand/25"
              >
                <a href="tel:+918886038800">
                  <Phone className="w-4 h-4 mr-1" />
                  Call +91 88860 38800
                </a>
              </Button>
            )}
            <Button
              onClick={() => openBooking("appointment")}
              data-testid="seo-book-btn"
              className={
                page.heroCall
                  ? "h-12 px-6 rounded-full border border-slate-300 bg-white text-[#0b2c4d] hover:bg-slate-50 font-semibold shadow-none"
                  : "bg-brand hover:bg-brand-hover text-white font-semibold h-12 px-6 rounded-full group shadow-lg shadow-brand/25"
              }
            >
              Book Appointment
              <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
            </Button>
            {!page.heroCall && (
            <Button
              onClick={() => openBooking("mri_review")}
              variant="outline"
              data-testid="seo-mri-btn"
              className="h-12 px-6 rounded-full border-slate-300 bg-white text-[#0b2c4d] hover:bg-slate-50 hover:text-[#0b2c4d]"
            >
              <FileImage className="w-4 h-4 mr-1" />
              Send MRI for Review
            </Button>
            )}
          </div>
        </div>
      </section>

      {/* Body content */}
      <article className="bg-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="text-lg text-slate-700 leading-relaxed">{renderRichText(page.intro)}</p>

          {page.sections.map((s) => (
            <div key={s.heading} className="mt-12">
              <h2 className="font-heading font-extrabold text-[#0b2c4d] text-2xl md:text-3xl tracking-tight">
                {s.heading}
              </h2>
              {s.body && (
                <p className="mt-4 text-slate-600 leading-relaxed">{renderRichText(s.body)}</p>
              )}
              {(s.paragraphs || []).map((para) => (
                <p key={para.slice(0, 40)} className="mt-4 text-slate-600 leading-relaxed">
                  {renderRichText(para)}
                </p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-2 list-disc pl-6 text-slate-600 leading-relaxed">
                  {s.list.map((item) => (
                    <li key={item.slice(0, 40)}>{renderRichText(item)}</li>
                  ))}
                </ul>
              )}
              {s.table && (
                <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="w-full text-sm text-left" data-testid={`seo-table-${s.heading.slice(0, 20)}`}>
                    <caption className="sr-only">{s.table.caption}</caption>
                    <thead>
                      <tr className="bg-slate-50">
                        {s.table.head.map((h, hi) => (
                          <th key={`${h}-${hi}`} className="px-4 py-3 font-heading font-bold text-[#0b2c4d] whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {s.table.rows.map((row) => (
                        <tr key={row[0]} className="border-t border-slate-100">
                          {row.map((cell, ci) => (
                          <td key={`${row[0]}-${ci}`} className={`px-4 py-3 text-slate-600 ${ci === 0 ? "font-semibold text-[#0b2c4d]" : ""}`}>
                            {renderRichText(cell)}
                          </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {s.note && (
                <p className="mt-3 text-xs text-slate-400 leading-relaxed">{s.note}</p>
              )}
              {s.image && (
                /* TODO: replace with de-identified MRI image when available */
                <figure className="mt-6" data-testid="section-illustration">
                  <img
                    src={s.image.src}
                    alt={s.image.alt}
                    loading="lazy"
                    className="w-full rounded-2xl border border-slate-200 bg-white"
                  />
                  <figcaption className="mt-3 text-sm text-slate-400">{s.image.caption}</figcaption>
                </figure>
              )}
              {s.video && (
                <figure className="mt-6" data-testid="section-video">
                  <div className="relative w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-900" style={{ paddingBottom: "56.25%" }}>
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${s.video.youtube}`}
                      title={s.video.title}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    />
                  </div>
                  <figcaption className="mt-3 text-sm text-slate-400">{s.video.caption}</figcaption>
                </figure>
              )}
              {s.review && (
                <figure
                  data-testid="section-review"
                  className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-6 py-6"
                >
                  <div className="flex items-center gap-1" aria-label={`${s.review.rating} star rating`}>
                    {Array.from({ length: s.review.rating }).map((_, si) => (
                      <Star key={si} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <blockquote className="mt-3 text-slate-600 leading-relaxed">
                    “{s.review.text}”
                  </blockquote>
                  <figcaption className="mt-4 text-sm">
                    <span className="font-heading font-bold text-[#0b2c4d]">{s.review.name}</span>
                    <span className="text-slate-400"> — {s.review.meta}</span>
                  </figcaption>
                  <a
                    href={DOCTOR.reviewsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="section-review-gmb-link"
                    className="mt-3 inline-flex items-center gap-1 text-sm text-brand font-semibold hover:underline underline-offset-4"
                  >
                    Read more reviews on Google
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                </figure>
              )}
              {s.placeholder && (
                <div
                  data-testid="content-placeholder"
                  className="mt-6 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center text-sm text-slate-400 font-medium"
                >
                  {s.placeholder}
                </div>
              )}
              {s.after && (
                <p className="mt-4 text-slate-600 leading-relaxed">{renderRichText(s.after)}</p>
              )}
              {s.mriCta && (
                <p className="mt-4 text-slate-700 leading-relaxed font-medium">
                  For a personalised estimate,{" "}
                  <button
                    type="button"
                    data-testid="cost-mri-review-link"
                    onClick={() => openBooking("mri_review")}
                    className="text-brand font-semibold underline underline-offset-4 decoration-brand/40 hover:decoration-brand transition-colors"
                  >
                    send your MRI for review
                  </button>{" "}
                  or call{" "}
                  <a href="tel:+918886038800" className="text-brand font-semibold whitespace-nowrap">
                    +91 88860 38800
                  </a>
                  .
                </p>
              )}
              {s.ctasCall && (
                <div className="mt-6 flex flex-col sm:flex-row flex-wrap items-center gap-3">
                  <Button
                    asChild
                    data-testid="emergency-call-btn"
                    className="bg-brand hover:bg-brand-hover text-white font-semibold h-12 px-6 rounded-full shadow-lg shadow-brand/25"
                  >
                    <a href="tel:+918886038800">
                      <Phone className="w-4 h-4 mr-1" />
                      Call +91 88860 38800
                    </a>
                  </Button>
                  <Button
                    onClick={() => openBooking("appointment")}
                    variant="outline"
                    data-testid="emergency-book-btn"
                    className="h-12 px-6 rounded-full border-slate-300 bg-white text-[#0b2c4d] hover:bg-slate-50 hover:text-[#0b2c4d]"
                  >
                    Book Appointment
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
              {s.ctas && (
                <div className="mt-6 flex flex-col sm:flex-row flex-wrap items-center gap-3">
                  <Button
                    onClick={() => openBooking("mri_review")}
                    data-testid="cost-mri-btn"
                    className="bg-brand hover:bg-brand-hover text-white font-semibold h-12 px-6 rounded-full group shadow-lg shadow-brand/25"
                  >
                    <FileImage className="w-4 h-4 mr-1" />
                    Send MRI for Review
                  </Button>
                  <Button
                    onClick={() => openBooking("appointment")}
                    variant="outline"
                    data-testid="cost-book-btn"
                    className="h-12 px-6 rounded-full border-slate-300 bg-white text-[#0b2c4d] hover:bg-slate-50 hover:text-[#0b2c4d]"
                  >
                    Book Appointment
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Button>
                  <a
                    href="tel:+918886038800"
                    data-testid="cost-call-link"
                    className="text-brand font-semibold whitespace-nowrap hover:underline underline-offset-4"
                  >
                    Call +91 88860 38800
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </article>

      {/* Services (optional, e.g. Madinaguda) */}
      {page.services && (
        <section className="bg-white pb-16 md:pb-20" data-testid="seo-services-section">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <h2 className="font-heading font-extrabold text-[#0b2c4d] text-3xl md:text-4xl tracking-tight">
              {page.services.heading}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-3xl">{page.services.intro}</p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {page.services.items.map((item) => (
                <Link
                  key={item.slug}
                  to={`/${item.slug}`}
                  data-testid={`service-link-${item.slug}`}
                  className="group glass-card rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
                >
                  <h3 className="font-heading font-extrabold text-[#0b2c4d] text-lg tracking-tight leading-snug group-hover:text-brand transition-colors">
                    {item.name}
                  </h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand font-semibold">
                    Learn more
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-slate-50 py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">FAQ</p>
          <h2 className="font-heading font-extrabold text-[#0b2c4d] text-3xl md:text-4xl tracking-tight mt-3">
            Frequently asked questions
          </h2>
          <div className="mt-8 space-y-3">
            {page.faqs.map((f, i) => (
              <details
                key={f.q}
                name="seo-faq"
                data-testid={`seo-faq-${i}`}
                className="group bg-white rounded-2xl px-6 border border-slate-200"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden text-left text-[#0b2c4d] hover:text-brand py-5 font-heading font-bold transition-colors">
                  {f.q}
                  <ChevronDown className="w-4 h-4 shrink-0 text-slate-400 transition-transform duration-200 group-open:rotate-180" />
                </summary>
                <div className="text-slate-500 leading-relaxed pb-5">{f.a}</div>
              </details>
            ))}
          </div>
          {page.type === "condition" && (
            <p className="mt-8 text-slate-600 leading-relaxed" data-testid="faq-doctor-guide-link">
              Not sure which doctor you need?{" "}
              <Link
                to="/neurologist-vs-neurosurgeon"
                className="text-brand font-semibold underline underline-offset-4 decoration-brand/40 hover:decoration-brand transition-colors"
              >
                Neurologist or neurosurgeon? Which doctor to see
              </Link>
            </p>
          )}
        </div>
      </section>

      {/* Medically reviewed + References */}
      {page.reviewBox && (
        <section className="bg-white py-14 md:py-16" data-testid="review-references-section">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-6 py-5 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-brand shrink-0 mt-0.5" />
              <p className="text-sm text-slate-600 leading-relaxed">
                <span className="font-heading font-bold text-[#0b2c4d]">Medically reviewed</span> by
                Dr. Arun Reddy M, Neurosurgeon &amp; Endoscopic Spine Surgeon (MBBS, DNB Neurosurgery,
                Fellowship in Endoscopic Spine Surgery). Last reviewed: {page.reviewDate}.
              </p>
            </div>
            {page.references && (
              <div className="mt-8">
                <h2 className="font-heading font-extrabold text-[#0b2c4d] text-2xl tracking-tight">
                  References
                </h2>
                <ol className="mt-4 space-y-2 list-decimal pl-6 text-sm text-slate-600 leading-relaxed">
                  {page.references.map((r) => (
                    <li key={r.url}>
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand underline underline-offset-4 decoration-brand/40 hover:decoration-brand transition-colors"
                      >
                        {r.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Related treatments (condition pages) */}
      {page.relatedTreatments && page.relatedTreatments.length > 0 && (
        <section className="bg-slate-50 py-16 md:py-20" data-testid="related-treatments-section">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Treatment Options</p>
            <h2 className="font-heading font-extrabold text-[#0b2c4d] text-3xl md:text-4xl tracking-tight mt-3">
              Related treatments
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {page.relatedTreatments.map((r) => (
                <Link
                  key={r}
                  to={`/${r}`}
                  data-testid={`related-treatment-${r}`}
                  className="group bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
                >
                  <div className="text-xs uppercase tracking-wider text-brand font-bold">
                    {SEO_PAGES[r].eyebrow}
                  </div>
                  <h3 className="mt-2 font-heading font-extrabold text-[#0b2c4d] text-lg tracking-tight leading-snug group-hover:text-brand transition-colors inline-flex items-start gap-1">
                    {PAGE_LABELS[r]}
                    <ArrowUpRight className="w-4 h-4 mt-1 shrink-0 text-brand transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related articles (condition pages) */}
      {page.relatedPosts && page.relatedPosts.length > 0 && (
        <section className="bg-white py-16 md:py-20" data-testid="related-articles-section">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Patient Education</p>
            <h2 className="font-heading font-extrabold text-[#0b2c4d] text-3xl md:text-4xl tracking-tight mt-3">
              Related articles
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {page.relatedPosts.map((r) => (
                <Link
                  key={r}
                  to={`/blog/${r}`}
                  data-testid={`related-article-${r}`}
                  className="group glass-card rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
                >
                  <div className="text-xs uppercase tracking-wider text-brand font-bold">
                    {BLOG_POSTS[r].category}
                  </div>
                  <h3 className="mt-2 font-heading font-extrabold text-[#0b2c4d] text-lg tracking-tight leading-snug">
                    {BLOG_POSTS[r].title}
                  </h3>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand font-semibold">
                    Read article
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      {page.related && page.related.length > 0 && (
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Related</p>
            <h2 className="font-heading font-extrabold text-[#0b2c4d] text-3xl md:text-4xl tracking-tight mt-3">
              Explore more
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {page.related.map((r) => (
                <Link
                  key={r}
                  to={`/${r}`}
                  data-testid={`related-${r}`}
                  className="group glass-card rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
                >
                  <div className="text-xs uppercase tracking-wider text-brand font-bold">
                    {SEO_PAGES[r].eyebrow}
                  </div>
                  <h3 className="mt-2 font-heading font-extrabold text-[#0b2c4d] text-lg tracking-tight leading-snug">
                    {PAGE_LABELS[r]}
                  </h3>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand font-semibold">
                    {PAGE_LABELS[r]}
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="bg-brand py-20 md:py-24 text-center">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <h2 className="font-heading font-extrabold text-white text-3xl md:text-4xl tracking-tight">
            Discuss your case with Dr. Arun Reddy M
          </h2>
          <p className="mt-4 text-white/85 max-w-xl mx-auto">
            Personalised evaluation at CARE Hospitals, Gachibowli, Hyderabad. Every plan is
            evidence-based and made openly with you.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Button
              onClick={() => openBooking("appointment")}
              data-testid="seo-cta-book-btn"
              className="bg-white hover:bg-slate-100 text-brand font-semibold px-7 py-6 rounded-full"
            >
              Book Appointment
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <a href={`tel:${DOCTOR.phoneRaw}`}>
              <Button variant="outline" className="px-7 py-6 rounded-full border-white/50 bg-transparent text-white hover:bg-white/10 hover:text-white w-full">
                Call {DOCTOR.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default SeoPage;
